import debounce from 'lodash.debounce';
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { mbToNumUtf16Chars } from '../../../libTs/depless';

export const LOCAL_STORAGE_APP_LIMIT_MB = 3;
export const LOCAL_STORAGE_APP_LIMIT_UTF16_CHARS = mbToNumUtf16Chars(
  LOCAL_STORAGE_APP_LIMIT_MB
);

export type AutosaveT = {
  id: string;
  timeCreated: number; // When the autosave was created.
  timeUpdated: number; // Last update timestamp.
  data: any; // The actual autosave data (cleared when permanently deleted).
  pathname: string; // Associated URL path.
  subpath: string; // Identifier for which form this autosave belongs to.
  trashedAt?: number; // When the autosave was trashed (i.e. soft-deleted).
  permanentlyDeletedAt?: number; // Tombstone marker for permanent deletion.
};

type AutosaveContextValue = {
  autosaves: AutosaveT[];
  addAutosave: (autosave: AutosaveT) => void;
  // Archive (soft-delete) an autosave.
  trashAutosave: (id: string) => void;
  updateAutosave: (id: string, newData: any) => void;
  debouncedUpdateAutosave: (id: string, newData: any) => void;
  trashAllAutosavesMaybe: () => void;
  // Permanently delete an autosave (leaves a tombstone to be purged later).
  // Pass the exact confirmation string to bypass the prompt
  deleteAutosavePermanently: (id: string, bypassConfirmation?: string) => void;
  // Trash and permanently delete an autosave atomically (no confirmation).
  trashAndDeleteAutosavePermanently: (id: string) => void;
  // Clear all tombstone records (permanently deleted autosave IDs)
  clearDeletedAutosaveIds: () => void;
};

const AutosaveContext = createContext<AutosaveContextValue | undefined>(
  undefined
);

// --- Utility Functions ---

// Transform an autosave object to trash and mark for permanent deletion
const trashAndDeleteAutosaveObj = (autosave: AutosaveT): AutosaveT => ({
  ...autosave,
  data: null,
  trashedAt: autosave.trashedAt || Date.now(),
  permanentlyDeletedAt: autosave.permanentlyDeletedAt || Date.now(),
});

// Generates a storage usage message for alerts.
const generateStorageUsageMessage = (categorizedSizes: {
  gen2Autosaves: number;
  gen1Autosaves: number;
  other: number;
}) => {
  return (
    `Current storage usage:\n` +
    `Gen-2 App Autosaves: ${categorizedSizes.gen2Autosaves} characters\n` +
    `Gen-1 Autosaves: ${categorizedSizes.gen1Autosaves} characters\n` +
    `Other: ${categorizedSizes.other} characters`
  );
};

// Calculate size of all localStorage items except 'autosaves'
const calculateLocalStorageSize = () => {
  return Object.keys(localStorage).reduce((total, key) => {
    if (key !== 'autosaves') {
      const item = localStorage.getItem(key);
      return total + (item ? item.length : 0);
    }
    return total;
  }, 0);
};

// Categorize localStorage items for usage reporting.
const categorizeLocalStorageItems = () => {
  return Object.keys(localStorage).reduce(
    (acc, key) => {
      const item = localStorage.getItem(key);
      const size = item ? item.length : 0;
      if (key === 'autosaves') {
        acc.gen2Autosaves += size;
      } else if (key === 'drafts') {
        acc.gen1Autosaves += size;
      } else {
        acc.other += size;
      }
      return acc;
    },
    { gen2Autosaves: 0, gen1Autosaves: 0, other: 0 }
  );
};

// Returns the effective update time for a record.
const getEffectiveUpdateTime = (record: AutosaveT): number =>
  Math.max(
    record.timeUpdated || 0,
    record.trashedAt || 0,
    record.permanentlyDeletedAt || 0
  );

// Merge two autosave arrays by id, favoring the record with the later effective update time.
const mergeAutosaves = (
  current: AutosaveT[],
  incoming: AutosaveT[]
): AutosaveT[] => {
  const merged: { [id: string]: AutosaveT } = {};

  // Add all current records.
  current.forEach((record) => {
    merged[record.id] = record;
  });

  // Merge incoming records.
  incoming.forEach((record) => {
    if (merged[record.id]) {
      const existing = merged[record.id];
      const existingTime = getEffectiveUpdateTime(existing);
      const incomingTime = getEffectiveUpdateTime(record);
      if (incomingTime > existingTime) {
        merged[record.id] = record;
      } else if (
        incomingTime === existingTime &&
        JSON.stringify(existing) !== JSON.stringify(record)
      ) {
        console.error(
          `Conflict detected for autosave ${record.id}: equal update times with differing data.`
        );
      }
    } else {
      merged[record.id] = record;
    }
  });

  return Object.values(merged);
};

type AutosaveStorage = {
  autosaves: AutosaveT[];
  lastTombstoneClearTime?: number;
};

export const AutosaveProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  // Initialize state from localStorage.
  const [autosaves, setAutosaves] = useState<AutosaveT[]>(() => {
    const stored = localStorage.getItem('autosaves');
    if (!stored) return [];

    try {
      const parsed = JSON.parse(stored);
      // Handle legacy format (just array) vs new format (object with metadata)
      return Array.isArray(parsed) ? parsed : parsed.autosaves || [];
    } catch {
      return [];
    }
  });

  const [lastTombstoneClearTime, setLastTombstoneClearTime] = useState<number>(() => {
    const stored = localStorage.getItem('autosaves');
    if (!stored) return 0;

    try {
      const parsed = JSON.parse(stored);
      return Array.isArray(parsed) ? 0 : parsed.lastTombstoneClearTime || 0;
    } catch {
      return 0;
    }
  });


  // Subscribe to localStorage changes from other tabs.
  useEffect(() => {
    const onStorageChange = (event: StorageEvent) => {
      if (event.key === 'autosaves') {
        if (!event.newValue) return;

        try {
          const parsed = JSON.parse(event.newValue);
          const incomingAutosaves = Array.isArray(parsed) ? parsed : parsed.autosaves || [];
          const incomingClearTime = Array.isArray(parsed) ? 0 : parsed.lastTombstoneClearTime || 0;

          setAutosaves((current) => mergeAutosaves(current, incomingAutosaves));

          // Update clear time if incoming is more recent
          setLastTombstoneClearTime((currentClearTime) => {
            const newClearTime = Math.max(currentClearTime, incomingClearTime);

            // If incoming clear time is newer, remove tombstones from current state
            if (incomingClearTime > currentClearTime) {
              setAutosaves((prev) => prev.filter((autosave) => !autosave.permanentlyDeletedAt));
            }

            return newClearTime;
          });
        } catch (error) {
          console.error('Error parsing autosaves from storage event:', error);
        }
      }
    };
    window.addEventListener('storage', onStorageChange);
    return () => window.removeEventListener('storage', onStorageChange);
  }, []);

  // Track the last persisted value to prevent cross-tab feedback loops
  const lastPersistedRef = useRef<string>('');

  // Persist autosaves to localStorage whenever they change.
  useEffect(() => {
    const storage: AutosaveStorage = {
      autosaves,
      lastTombstoneClearTime: lastTombstoneClearTime || undefined,
    };
    const serialized = JSON.stringify(storage);

    // Only write to localStorage if the data actually changed
    // This prevents cross-tab sync loops where mergeAutosaves creates new array references
    if (serialized !== lastPersistedRef.current) {
      localStorage.setItem('autosaves', serialized);
      lastPersistedRef.current = serialized;
    }
  }, [autosaves, lastTombstoneClearTime]);

  // --- Autosave Operations ---

  // Add a new autosave, ensuring we don't exceed storage limits.
  const addAutosave = useCallback((autosave: AutosaveT) => {
    setAutosaves((prevAutosaves) => {
      if (prevAutosaves.some((a) => a.id === autosave.id)) {
        throw new Error('An autosave with that ID already exists');
      }

      const newAutosave: AutosaveT = {
        ...autosave,
        trashedAt: undefined,
        permanentlyDeletedAt: undefined,
      };

      let candidateState = [...prevAutosaves, newAutosave];
      let candidateJson = JSON.stringify(candidateState);
      const removedAutosaves: AutosaveT[] = [];

      const otherLocalStorageSize = calculateLocalStorageSize();
      const categorizedSizes = categorizeLocalStorageItems();

      // First check if we need to make space
      if (otherLocalStorageSize + candidateJson.length > LOCAL_STORAGE_APP_LIMIT_UTF16_CHARS) {
        // Clear all existing tombstones first to make room for new ones
        candidateState = candidateState.filter((a) => !a.permanentlyDeletedAt);
        candidateJson = JSON.stringify(candidateState);

        // Now loop through trashed items if we still need space
        while (
          otherLocalStorageSize + candidateJson.length >
          LOCAL_STORAGE_APP_LIMIT_UTF16_CHARS
        ) {
          // First try trashed items
          let removable = candidateState.filter((a) => a.trashedAt && !a.permanentlyDeletedAt);

          // If no trashed items, fall back to active autosaves
          if (removable.length === 0) {
            removable = candidateState.filter((a) => !a.trashedAt && !a.permanentlyDeletedAt && a.id !== newAutosave.id);
          }

          if (removable.length === 0) {
            alert(
              `Storage limit reached and no removable autosaves available. Cannot add new autosave. Please clear autosaves manually. Returning to previous page.\n\n` +
                generateStorageUsageMessage(categorizedSizes)
            );
            window.history.back();
            return prevAutosaves;
          }

          const oldest = removable.reduce((prev, curr) => {
            const prevTime = prev.trashedAt || prev.timeUpdated;
            const currTime = curr.trashedAt || curr.timeUpdated;
            return prevTime < currTime ? prev : curr;
          });
          removedAutosaves.push(oldest);

          // Mark as permanently deleted (creating a new tombstone)
          candidateState = candidateState.map((a) =>
            a.id === oldest.id ? trashAndDeleteAutosaveObj(a) : a
          );
          candidateJson = JSON.stringify(candidateState);
        }
      }

      if (removedAutosaves.length > 0) {
        // TODO: Consider offering download of autosaves before deletion
        // Could prompt: "Storage is full. Download backup before deleting?"
        // Then: downloadAutosavesAsJSON(removedAutosaves, `autosaves-backup-${Date.now()}.json`)

        const trashedItems = removedAutosaves.filter((a) => a.trashedAt);
        const activeItems = removedAutosaves.filter((a) => !a.trashedAt);

        let confirmMsg = 'To open a new autosave, we need to:\n\n';

        if (trashedItems.length > 0) {
          const trashedDates = trashedItems
            .map((a) => new Date(a.trashedAt!).toLocaleString())
            .join(', ');
          confirmMsg += `- Delete ${trashedItems.length} trashed autosave${trashedItems.length > 1 ? 's' : ''} (dated: ${trashedDates})\n`;
        }

        if (activeItems.length > 0) {
          const activeDates = activeItems
            .map((a) => new Date(a.timeUpdated).toLocaleString())
            .join(', ');
          confirmMsg += `- Delete ${activeItems.length} active autosave${activeItems.length > 1 ? 's' : ''} (created: ${activeDates})\n`;
        }

        confirmMsg += `\nOK to proceed; Cancel will navigate you back.\n\n${generateStorageUsageMessage(categorizedSizes)}`;

        if (!window.confirm(confirmMsg)) {
          window.history.back();
          return prevAutosaves;
        }
      }

      return candidateState;
    });
  }, []);

  // Update an autosave with new data and “revive” it by clearing any trash or permanent deletion markers.
  const updateAutosave = useCallback((id: string, newData: any) => {
    setAutosaves((prev) =>
      prev.map((a) =>
        a.id === id
          ? {
              ...a,
              data: newData,
              timeUpdated: Date.now(),
              trashedAt: undefined,
              permanentlyDeletedAt: undefined,
            }
          : a
      )
    );
  }, []);

  // Archive (soft-delete) an autosave by clearing its data and setting trashedAt.
  const trashAutosave = useCallback((id: string) => {
    setAutosaves((prev) =>
      prev.map((a) =>
        a.id === id
          ? {
              ...a,
              data: null,
              trashedAt: a.trashedAt || Date.now(),
            }
          : a
      )
    );
  }, []);

  // Permanently delete an autosave by marking it with permanentlyDeletedAt.
  // (Note: the record remains as a tombstone and will be purged after a month.)
  const deleteAutosavePermanently = useCallback((id: string, bypassConfirmation?: string) => {
    const REQUIRED_CONFIRMATION = 'DELETE';

    // If bypass confirmation is provided, check if it matches the required text
    let confirmed = false;
    if (bypassConfirmation) {
      // Check if the bypass string contains the required confirmation
      confirmed = bypassConfirmation === 'PERMANENTLY DELETE ALL TRASHED AUTOSAVES';
    } else {
      // Show prompt for individual deletion
      const confirmation = window.prompt(
        "Type 'DELETE' to permanently delete this autosave."
      );
      confirmed = confirmation === REQUIRED_CONFIRMATION;

      if (!confirmed && confirmation !== null) {
        alert('Confirmation text did not match. Autosave not deleted.');
        return;
      }
    }

    if (confirmed) {
      setAutosaves((prev) =>
        prev.map((a) =>
          a.id === id
            ? {
                ...a,
                data: null,
                permanentlyDeletedAt: a.permanentlyDeletedAt || Date.now(),
              }
            : a
        )
      );
    }
  }, []);

  // Trash and permanently delete an autosave atomically (for programmatic cleanup).
  // If already trashed, preserves the original trashedAt timestamp.
  // Used internally for storage cleanup - no confirmation prompts.
  const trashAndDeleteAutosavePermanently = useCallback((id: string) => {
    setAutosaves((prev) =>
      prev.map((a) => (a.id === id ? trashAndDeleteAutosaveObj(a) : a))
    );
  }, []);

  // Clear all tombstone records (permanently deleted autosave IDs)
  const clearDeletedAutosaveIds = useCallback(() => {
    const clearTime = Date.now();
    setLastTombstoneClearTime(clearTime);
    setAutosaves((prev) =>
      prev.filter((autosave) => !autosave.permanentlyDeletedAt)
    );
  }, []);

  // Debounce updates to avoid rapid-fire state changes.
  const debouncedUpdateAutosave = useMemo(
    () => debounce(updateAutosave, 500),
    [updateAutosave]
  );

  // Flush pending debounced updates on unmount.
  useEffect(() => {
    return () => {
      debouncedUpdateAutosave.flush();
    };
  }, [debouncedUpdateAutosave]);

  // Periodically purge autosaves that have been permanently deleted for over a month.
  useEffect(() => {
    const oneMonth = 30 * 24 * 60 * 60 * 1000;
    const interval = setInterval(() => {
      setAutosaves((prev) =>
        prev.filter(
          (a) =>
            !a.permanentlyDeletedAt ||
            Date.now() - a.permanentlyDeletedAt < oneMonth
        )
      );
    }, 60000); // Check every minute.
    return () => clearInterval(interval);
  }, []);

  // Archive all autosaves (a bulk "delete" action).
  const trashAllAutosavesMaybe = useCallback(() => {
    const confirmation = window.prompt(
      "Type 'TRASH ALL' to trash all autosaves (they will be kept for 7 days)."
    );
    if (confirmation === 'TRASH ALL') {
      setAutosaves((prev) =>
        prev.map((a) =>
          a.trashedAt ? a : { ...a, trashedAt: Date.now() }
        )
      );
    } else {
      alert('Confirmation text did not match. Autosaves not trashed.');
    }
  }, []);

  const autosaveContextValue: AutosaveContextValue = useMemo(
    () => ({
      addAutosave,
      autosaves,
      debouncedUpdateAutosave,
      trashAllAutosavesMaybe,
      trashAutosave,
      deleteAutosavePermanently,
      trashAndDeleteAutosavePermanently,
      clearDeletedAutosaveIds,
      updateAutosave,
    }),
    [
      addAutosave,
      autosaves,
      debouncedUpdateAutosave,
      trashAllAutosavesMaybe,
      trashAutosave,
      deleteAutosavePermanently,
      trashAndDeleteAutosavePermanently,
      clearDeletedAutosaveIds,
      updateAutosave,
    ]
  );

  return (
    <AutosaveContext.Provider value={autosaveContextValue}>
      {children}
    </AutosaveContext.Provider>
  );
};

export const useAutosaves = () => {
  const context = useContext(AutosaveContext);
  if (!context) {
    throw new Error('useAutosaves must be used within an AutosaveProvider');
  }
  return context;
};